import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserProvider, useUser } from "./context/UserContext";
import { Toaster } from "./components/ui/sonner";
import PublicSite from "./pages/PublicSite";
import BooksPage from "./pages/BooksPage";
import TermsPage from "./pages/TermsPage";
import ResetPassword from "./pages/ResetPassword";
import CrmLogin from "./components/crm/CrmLogin";
import CrmLayout from "./components/crm/CrmLayout";
import Dashboard from "./components/crm/Dashboard";
import Leads from "./components/crm/Leads";
import Clients from "./components/crm/Clients";
import Appointments from "./components/crm/Appointments";
import Products from "./components/crm/Products";
import Invoices from "./components/crm/Invoices";
import Messages from "./components/crm/Messages";
import Users from "./components/crm/Users";
import UserDocuments from "./components/crm/UserDocuments";
import Notes from "./components/crm/Notes";
import CrmSettings from "./components/crm/CrmSettings";
import CrmBooks from "./components/crm/Books";
import PortalLayout from "./components/portal/PortalLayout";
import PortalOverview from "./components/portal/PortalOverview";
import PortalMessages from "./components/portal/PortalMessages";
import PortalInvoices from "./components/portal/PortalInvoices";
import PortalDocuments from "./components/portal/PortalDocuments";
import PayInvoice from "./components/portal/PayInvoice";
import PortalProfile from "./components/portal/PortalProfile";
import PortalActivity from "./components/portal/PortalActivity";
import PortalCreditAppointments from "./components/portal/PortalCreditAppointments";
import PortalBooks from "./components/portal/PortalBooks";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/crm/login" replace />;
  return children;
}

function ModuleRoute({ module, adminOnly, children }) {
  const { user } = useAuth();
  if (user?.role === "admin") return children;
  if (adminOnly) return <Navigate to="/crm" replace />;
  if (module && !(user?.modules || []).includes(module)) return <Navigate to="/crm" replace />;
  return children;
}

function PortalRoute({ children }) {
  const { user, loading } = useUser();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <UserProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicSite />} />
            <Route path="/books" element={<BooksPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/crm/login" element={<CrmLogin />} />
            <Route
              path="/crm"
              element={
                <ProtectedRoute>
                  <CrmLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<ModuleRoute module="leads"><Leads /></ModuleRoute>} />
              <Route path="clients" element={<ModuleRoute module="clients"><Clients /></ModuleRoute>} />
              <Route path="appointments" element={<ModuleRoute module="appointments"><Appointments /></ModuleRoute>} />
              <Route path="products" element={<ModuleRoute module="products"><Products /></ModuleRoute>} />
              <Route path="invoices" element={<ModuleRoute module="invoices"><Invoices /></ModuleRoute>} />
              <Route path="messages" element={<ModuleRoute module="messages"><Messages /></ModuleRoute>} />
              <Route path="users" element={<ModuleRoute adminOnly><Users /></ModuleRoute>} />
              <Route path="documents" element={<ModuleRoute module="documents"><UserDocuments /></ModuleRoute>} />
              <Route path="notes" element={<ModuleRoute module="notes"><Notes /></ModuleRoute>} />
              <Route path="settings" element={<ModuleRoute adminOnly><CrmSettings /></ModuleRoute>} />
              <Route path="books" element={<ModuleRoute adminOnly><CrmBooks /></ModuleRoute>} />
            </Route>
            <Route
              path="/portal"
              element={
                <PortalRoute>
                  <PortalLayout />
                </PortalRoute>
              }
            >
              <Route index element={<PortalOverview />} />
              <Route path="messages" element={<PortalMessages />} />
              <Route path="invoices" element={<PortalInvoices />} />
              <Route path="documents" element={<PortalDocuments />} />
              <Route path="activity" element={<PortalActivity />} />
              <Route path="credit-appointments" element={<PortalCreditAppointments />} />
              <Route path="books" element={<PortalBooks />} />
              <Route path="pay/:id" element={<PayInvoice />} />
              <Route path="profile" element={<PortalProfile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </UserProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
